import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Spin from "components/Spin/Spin";
import { getBankInfoConfig, updateBankInfoConfig } from "./model";
import s from "./PayfacDetails.module.scss";

const BANK_INFO_LOCALE_KEY = "page.payfacs.payfacDetail.bankInfoConfig.";
const BANK_INFO_GRID_STYLE = { marginBottom: 0 };
const BANK_SECTIONS = [
  {
    key: "dbs",
    fields: [
      { key: "organizationId", path: ["dbs", "organizationId"] },
      { key: "senderName", path: ["dbs", "senderName"] },
      { key: "hkdBankAccount", path: ["dbs", "bankAccountByCurrency", "HKD"] },
      { key: "usdBankAccount", path: ["dbs", "bankAccountByCurrency", "USD"] },
    ],
  },
  {
    key: "citic",
    fields: [
      {
        key: "fundingAccountNumber",
        path: ["citic", "fundingAccountNumber"],
      },
    ],
  },
  {
    key: "fubon",
    fields: [
      { key: "payerAccount", path: ["fubon", "payerAccount"] },
      {
        key: "serviceChargeDebitedAccount",
        path: ["fubon", "serviceChargeDebitedAccount"],
      },
    ],
  },
];

const createDefaultConfig = () => ({
  dbs: {
    organizationId: "",
    senderName: "",
    bankAccountByCurrency: {
      HKD: "",
      USD: "",
    },
  },
  citic: {
    fundingAccountNumber: "",
  },
  fubon: {
    payerAccount: "",
    serviceChargeDebitedAccount: "",
  },
});

const normalizeConfig = (config = {}) => {
  const defaults = createDefaultConfig();
  return {
    dbs: {
      ...defaults.dbs,
      ...(config.dbs || {}),
      bankAccountByCurrency: {
        ...defaults.dbs.bankAccountByCurrency,
        ...(config.dbs?.bankAccountByCurrency || {}),
      },
    },
    citic: {
      ...defaults.citic,
      fundingAccountNumber:
        config.citic?.fundingAccountNumber ?? config.citic?.seq2 ?? "",
    },
    fubon: {
      ...defaults.fubon,
      ...(config.fubon || {}),
    },
  };
};

const buildUpdatePayload = (config = {}) => {
  return {
    dbs: {
      organizationId: config.dbs?.organizationId ?? null,
      senderName: config.dbs?.senderName ?? null,
      bankAccountByCurrency: {
        HKD: config.dbs?.bankAccountByCurrency?.HKD ?? null,
        USD: config.dbs?.bankAccountByCurrency?.USD ?? null,
      },
    },
    citic: {
      fundingAccountNumber:
        config.citic?.fundingAccountNumber ?? config.citic?.seq2 ?? null,
    },
    fubon: {
      payerAccount: config.fubon?.payerAccount ?? null,
      serviceChargeDebitedAccount:
        config.fubon?.serviceChargeDebitedAccount ?? null,
    },
  };
};

const getValueByPath = (target, path) => {
  return path.reduce((value, key) => value?.[key], target) ?? "";
};

const setValueByPath = (target, path, value) => {
  const [currentKey, ...restPath] = path;
  if (!currentKey) {
    return target;
  }

  if (restPath.length === 0) {
    return {
      ...target,
      [currentKey]: value,
    };
  }

  return {
    ...target,
    [currentKey]: setValueByPath(target?.[currentKey] ?? {}, restPath, value),
  };
};

const PayfacDetailsBankInfoConfig = ({ payfacId }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [config, setConfig] = useState(() => createDefaultConfig());
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const isFormDisabled = loading || updating || !payfacId;

  const snackbar = (message, variant = "success") => {
    enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: 2000,
    });
  };

  useEffect(() => {
    let active = true;

    const fetchBankInfoConfig = async () => {
      if (!payfacId) {
        setConfig(createDefaultConfig());
        setLoading(false);
        return;
      }

      setLoading(true);
      const apiResponse = await getBankInfoConfig(payfacId);
      if (!active) return;

      if (!apiResponse || !apiResponse?.data) {
        snackbar(t("general.message.networkError"), "error");
        setLoading(false);
        return;
      }

      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          setLoading(false);
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            setConfig(normalizeConfig(responseBody.data));
          } else {
            snackbar(
              responseBody.message || t("general.message.initial.fetch.error"),
              "error",
            );
            setConfig(createDefaultConfig());
          }
        }
      }

      setLoading(false);
    };

    fetchBankInfoConfig();

    return () => {
      active = false;
    };
  }, [payfacId]);

  const updateConfigField = (path, value) => {
    setConfig((state) => setValueByPath(state, path, value));
  };

  const handleUpdate = async () => {
    if (!payfacId) {
      snackbar(t("general.message.networkError"), "error");
      return;
    }

    setUpdating(true);
    const apiResponse = await updateBankInfoConfig(
      payfacId,
      buildUpdatePayload(config),
    );

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
      setUpdating(false);
      return;
    }

    switch (apiResponse.status) {
      case 401: {
        console.log("You have no authorization to view the page");
        setUpdating(false);
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        snackbar(t("general.message.unauthorized"), "error");
        break;
      }
      default: {
        const responseBody = apiResponse.data;
        if (responseBody.responseCode === "0000") {
          setConfig(normalizeConfig(responseBody.data));
          snackbar(t(BANK_INFO_LOCALE_KEY + "toast.update"));
        } else {
          snackbar(
            responseBody.message || t("general.message.networkError"),
            "error",
          );
        }
      }
    }

    setUpdating(false);
  };

  const renderSection = ({ key, fields }) => {
    return (
      <section key={key} className={s.bankInfoCard}>
        <div className={s.bankInfoCardHeader}>
          <div>
            <div className={s.bankInfoCardTitle}>
              {t(BANK_INFO_LOCALE_KEY + "bank." + key)}
            </div>
          </div>
        </div>
        <Grid gridId="PayfacDetailsGrid" style={BANK_INFO_GRID_STYLE}>
          {fields.map(({ key: fieldKey, path }) => {
            return (
              <OutlinedText
                key={fieldKey}
                label={t(BANK_INFO_LOCALE_KEY + "field." + fieldKey)}
                value={getValueByPath(config, path)}
                onchangeFn={(e) => updateConfigField(path, e.target.value)}
                disabled={isFormDisabled}
              />
            );
          })}
        </Grid>
      </section>
    );
  };

  return (
    <div className={s.contentContainer}>
      <div className={s.bankInfoConfigContainer}>
        {loading ? <Spin /> : null}
        {!loading ? (
          <>
            <div className={s.bankInfoConfigGrid}>
              {BANK_SECTIONS.map(renderSection)}
            </div>
            <div className={s.bankInfoActionContainer}>
              <ButtonMAS
                variant="primary"
                isLoading={updating}
                onClick={handleUpdate}
                disabled={isFormDisabled}
              >
                {t(BANK_INFO_LOCALE_KEY + "action.update")}
              </ButtonMAS>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(PayfacDetailsBankInfoConfig);
